import { userRef } from '@util/firestore/users';
import { logError } from '@util/logError';
import { doc, onSnapshot } from 'firebase/firestore';
import { UserDocument } from 'models/user';
import { useEffect, useState } from 'react';

const MAX_PERMISSION_RETRIES = 10;
const INITIAL_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 8000;

// can only be used by own user or support role
// undefined means loading
// null means not found
const useRealTimeUser = (uid?: string, refreshKey = 0) => {
  const [state, setState] = useState<{
    uid?: string;
    refreshKey?: number;
    user?: UserDocument | null;
  }>({});
  useEffect(() => {
    if (!uid) return;

    let unsubscribe: (() => void) | undefined;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;
    let cancelled = false;

    const subscribe = () => {
      unsubscribe = onSnapshot(
        doc(userRef, uid),
        (snapshot) => {
          retryCount = 0;
          const realTimeData = snapshot.data();
          setState({
            uid,
            refreshKey,
            user: (realTimeData as UserDocument | undefined) ?? null,
          });
        },
        (error) => {
          logError(error, undefined, undefined, 'useRealTimeUser: ' + uid);

          // New users cannot read their doc until it exists (see isSignedIn rule).
          const isPermissionDenied =
            'code' in error && error.code === 'permission-denied';
          if (
            !cancelled &&
            isPermissionDenied &&
            retryCount < MAX_PERMISSION_RETRIES
          ) {
            const delay = Math.min(
              INITIAL_RETRY_DELAY_MS * 2 ** retryCount,
              MAX_RETRY_DELAY_MS
            );
            retryCount += 1;
            retryTimeout = setTimeout(() => {
              unsubscribe?.();
              subscribe();
            }, delay);
          }
        }
      );
    };

    subscribe();

    return () => {
      cancelled = true;
      unsubscribe?.();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [uid, refreshKey]);
  if (!uid || state.uid !== uid || state.refreshKey !== refreshKey) {
    return undefined;
  }
  return state.user;
};

export default useRealTimeUser;
